import { useQuery } from '@tanstack/react-query';
import { Action, Card } from 'corso-admin-ui';
import api from '~/api';
import { MediaGallery } from '~/components/media/MediaGallery';
import { MediaPreview } from '~/components/media/MediaPreview';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useStoreId } from '~/providers/StoreUserProvider';

// TODO: does this use the provider version?
function useRegistration({ registrationId }: { registrationId?: string }) {
  const storeId = useStoreId();

  return useQuery({
    enabled: !!registrationId,
    queryKey: ['registration', { registrationId, storeId }],
    queryFn: () => {
      if (!registrationId) throw new Error('Missing Registration ID');
      return api.store(storeId).registrations.get({
        registrationId,
      });
    },
  });
}

type Registration = NonNullable<
  Awaited<ReturnType<typeof useClaim>>['originalStoreOrder']['registration']
>;

type ProofOfPurchaseFileUrls = Registration['proofOfPurchaseFileUrls'];

function ProofOfPurchaseDisplay({
  proofOfPurchaseFileUrls,
}: {
  proofOfPurchaseFileUrls: ProofOfPurchaseFileUrls;
}) {
  if (proofOfPurchaseFileUrls.length === 0) return null;

  const [proof] = proofOfPurchaseFileUrls;

  return (
    <div className="mt-2 flex flex-col gap-2">
      <Card.Heading>Proof of Purchase</Card.Heading>
      {proof && proofOfPurchaseFileUrls.length === 1 ? (
        <MediaPreview
          src={proof}
          alt="Proof of Purchase"
          expandable
          variant="auto"
        />
      ) : (
        <MediaGallery
          title="Proof of Purchase"
          items={proofOfPurchaseFileUrls.map((url) => ({
            src: url,
            alt: 'Proof of Purchase',
          }))}
        />
      )}
    </div>
  );
}

export function RegistrationSummary({
  registrationId,
}: {
  registrationId: Registration['id'];
}) {
  const storeId = useStoreId();

  const { data: registration, isPending } = useRegistration({
    registrationId: `${registrationId}`,
  });

  if (isPending || !registration) return null;

  const { externalId, proofOfPurchaseFileUrls } = registration;

  return (
    <Card>
      <div className="flex flex-col gap-1">
        <Card.Line>
          <Card.Heading>Registration</Card.Heading>
        </Card.Line>

        <div>
          <Action
            accessibilityLabel={`View registration #${externalId}`}
            to={`/${storeId}/registrations/${registration.id}`}
            variant="link"
            className="text-xs"
          >
            {`#${externalId}`}
          </Action>
        </div>

        <Card.Heading>Channel</Card.Heading>

        <p className="text-xs text-neutral-500">
          {registration.registrationChannelName}
        </p>
        <ProofOfPurchaseDisplay
          proofOfPurchaseFileUrls={proofOfPurchaseFileUrls}
        />
      </div>
    </Card>
  );
}
